import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useWallet } from '../../contexts/WalletContext';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { useSocialLinks } from '../../hooks/useSocialLinks';
import { styled, useTheme } from '@mui/material/styles';
import SocialLinks from '../Common/SocialLinks';
import HelpButton from '../Help/HelpButton';
import WalletModal from '../Wallet/WalletModal';
import DashboardFooter from './DashboardFooter';
import ProfileSettings from '../Profile/ProfileSettings';
import MobileBottomNav from './MobileBottomNav';
import {
  Box,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  Typography,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Menu,
  MenuItem,
  Breadcrumbs,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as ProjectsIcon,
  Timeline as AnalyticsIcon,
  Settings as SettingsIcon,
  ContactSupport as ContactIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccountBalanceWallet as WalletIcon,
  ContentCopy as CopyIcon,
  Logout as DisconnectIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
  Layers as LayersIcon,
  Person as PersonIcon,
  AccountTree as PathsIcon,
  Rule as RulesIcon,
  Visibility as PreviewIcon,
  Description as MetadataIcon,
  PlayArrow as GenerateIcon,
  History as ActivityIcon,
  BarChart as ChartIcon,
  FolderSpecial as FolderSpecialIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
  Shield as ShieldIcon,
  Brush as CustomIcon,
  Movie as MovieIcon,
  Image as ImageIcon,
  Rocket as BuildIcon,
  Group as TeamIcon,
  Note as NotesIcon,
  Assessment as RarityIcon
} from '@mui/icons-material';
import { db } from '../../lib/supabase';
import { projects as apiProjects } from '../../lib/api';
import { theme, commonStyles } from '../../styles/theme';
import { isAnimatedProject } from '../../utils/animatedNftHelpers';

const drawerWidth = 280; // Increased from 240 for better menu item spacing
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme: muiTheme, open }) => ({
    flexGrow: 1,
    padding: 0,
    [muiTheme.breakpoints.down('sm')]: {
      paddingTop: '56px', // Account for shorter mobile AppBar
    },
    transition: muiTheme.transitions.create('margin', {
      easing: muiTheme.transitions.easing.easeInOut,
      duration: muiTheme.transitions.duration.standard,
    }),
    marginLeft: 0,
    background: theme.colors.bgPrimary,
    minHeight: '100vh',
    ...(open && {
      transition: muiTheme.transitions.create('margin', {
        easing: muiTheme.transitions.easing.easeInOut,
        duration: muiTheme.transitions.duration.standard,
      }),
      marginLeft: `${drawerWidth}px`,
    }),
  })
);

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme: muiTheme, open }) => ({
  background: 'rgba(0, 0, 0, 0.95)',
  backdropFilter: 'blur(20px)',
  borderBottom: `1px solid ${theme.colors.primaryAlpha20}`,
  boxShadow: `0 4px 20px ${theme.colors.primaryAlpha10}`,
  transition: muiTheme.transitions.create(['margin', 'width'], {
    easing: muiTheme.transitions.easing.easeInOut,
    duration: muiTheme.transitions.duration.standard,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: muiTheme.transitions.create(['margin', 'width'], {
      easing: muiTheme.transitions.easing.easeInOut,
      duration: muiTheme.transitions.duration.standard,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme: muiTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: muiTheme.spacing(0, 2),
  ...muiTheme.mixins.toolbar,
  justifyContent: 'space-between',
  background: theme.colors.bgPrimary,
  borderBottom: `1px solid ${theme.colors.primaryAlpha30}`,
}));

const StyledDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    background: theme.colors.bgPrimary,
    borderRight: `1px solid ${theme.colors.primaryAlpha20}`,
    boxShadow: `4px 0 20px ${theme.colors.primaryAlpha10}`,
  },
}));

const GlassCard = styled(Box)(({ theme: muiTheme }) => ({
  background: theme.colors.bgCard,
  backdropFilter: 'blur(20px)',
  borderRadius: '12px',
  border: `1px solid ${theme.colors.borderSubtle}`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  padding: muiTheme.spacing(3),
  margin: muiTheme.spacing(2),
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 48px ${theme.colors.primaryAlpha30}`,
    border: `1px solid ${theme.colors.borderPrimary}`,
  },
}));

const defaultMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard' },
  { text: 'Projects', icon: <ProjectsIcon />, path: '/app/projects' },
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/app/analytics' },
  { text: 'Profile Settings', icon: <PersonIcon />, action: 'profile' }, // Special action instead of path
  { text: 'Contact Us', icon: <ContactIcon />, path: '/app/contact' },
];

const getProjectMenuItems = (projectId) => ({
  // BUILD section
  build: {
    title: 'Build',
    items: [
      { text: 'Layers', icon: <LayersIcon />, path: `/app/projects/${projectId}/layers` },
      { text: 'Paths', icon: <PathsIcon />, path: `/app/projects/${projectId}/paths` },
      { text: 'Rules', icon: <RulesIcon />, path: `/app/projects/${projectId}/rules` },
    ]
  },
  // GENERATE section
  generate: {
    title: 'Generate',
    items: [
      { text: 'Metadata', icon: <MetadataIcon />, path: `/app/projects/${projectId}/metadata` },
      { text: 'Preview', icon: <PreviewIcon />, path: `/app/projects/${projectId}/preview` },
      { text: 'Custom Builder', icon: <CustomIcon />, path: `/app/projects/${projectId}/custom` },
      { text: 'Generate', icon: <GenerateIcon />, path: `/app/projects/${projectId}/generate` },
    ]
  },
  // ANALYZE section
  analyze: {
    title: 'Analyze',
    items: [
      { text: 'Path Analytics', icon: <ChartIcon />, path: `/app/projects/${projectId}/path-analytics` },
      { text: 'Path Testing', icon: <PreviewIcon />, path: `/app/projects/${projectId}/visual-path-testing` },
      { text: 'Rarity Analysis', icon: <RarityIcon />, path: `/app/projects/${projectId}/rarity` },
      { text: 'Analytics', icon: <ChartIcon />, path: `/app/projects/${projectId}/analytics` },
      { text: 'NFT Exclusions', icon: <InfoIcon />, path: `/app/projects/${projectId}/exclusions` },
    ]
  },
  // PROJECT section
  project: {
    title: 'Project',
    items: [
      { text: 'Team', icon: <TeamIcon />, path: `/app/projects/${projectId}/team` },
      { text: 'Notes', icon: <NotesIcon />, path: `/app/projects/${projectId}/notes` },
      { text: 'Activity', icon: <ActivityIcon />, path: `/app/projects/${projectId}/activity` },
      { text: 'Payments', icon: <PaymentIcon />, path: `/app/projects/${projectId}/payments` },
      { text: 'Security', icon: <ShieldIcon />, path: `/app/projects/${projectId}/security` },
      { text: 'Settings', icon: <SettingsIcon />, path: `/app/projects/${projectId}/settings` },
    ]
  },
});

// Format wallet address to show first and last 4 characters
const formatAddress = (address) => {
  if (!address) return '';
  return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
};

export default function MainLayout() {
  const muiTheme = useTheme();
  const { userMode, setUserMode } = useUserPreferences();
  const { 
    connected, 
    walletAddress, 
    connectSolanaWallet, 
    connectEthereumWallet, 
    disconnectWallet,
    walletType,
    selectedNetwork,
    walletName,
    connectionStatus,
    getWalletInfo,
    connecting
  } = useWallet();
  
  // Get blockchain info
  const getBlockchainInfo = () => {
    const network = selectedNetwork || walletType;
    if (network === 'solana') {
      return {
        name: 'Solana',
        logo: 'https://bpzpmhytoyurlgptrpmm.supabase.co/storage/v1/object/public/system-assets/logos/solana.jpg',
        color: '#14F195'
      };
    } else if (network === 'abstract') {
      return {
        name: 'Abstract',
        logo: 'https://bpzpmhytoyurlgptrpmm.supabase.co/storage/v1/object/public/system-assets/logos/abstract.png',
        color: '#FF6B00'
      };
    } else {
      return {
        name: 'Ethereum',
        logo: 'https://bpzpmhytoyurlgptrpmm.supabase.co/storage/v1/object/public/system-assets/logos/ethereum.webp',
        color: '#627EEA'
      };
    }
  };
  
  const blockchainInfo = getBlockchainInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { socialLinks } = useSocialLinks();
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [sectionsOpen, setSectionsOpen] = useState({
    build: true,    // Open by default — primary workflow
    generate: false,
    analyze: false,
    project: false,
  });
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [profileSettingsOpen, setProfileSettingsOpen] = useState(false);

  // Redirect to homepage if not connected
  useEffect(() => {
    if (!connected) {
      navigate('/');
    }
  }, [connected, navigate]);

  // Extract project ID from URL
  const projectId = location.pathname.match(/\/projects\/([^/]+)/)?.[1];

  // Close drawer on mobile after navigation
  useEffect(() => {
    if (isMobile && open) {
      handleDrawerClose();
    }
  }, [location.pathname]); // Only location.pathname as dependency

  // Listen for openProfileSettings event from child components
  useEffect(() => {
    const handleOpenProfileSettings = () => {
      setProfileSettingsOpen(true);
    };
    
    window.addEventListener('openProfileSettings', handleOpenProfileSettings);
    return () => {
      window.removeEventListener('openProfileSettings', handleOpenProfileSettings);
    };
  }, []);

  const handleWalletMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (copied) {
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      handleClose();
    }
  };

  const handleDisconnect = () => {
    disconnectWallet();
    handleClose();
    navigate('/');
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleMobileNav = (action) => {
    if (action === 'menu') {
      handleDrawerOpen();
    } else if (action === 'profile') {
      setProfileSettingsOpen(true);
    }
  };

  // Fetch current project details (including mode)
  useEffect(() => {
    const fetchProject = async () => {
      // Skip fetching if projectId is "new" (new project creation)
      if (projectId && projectId !== 'new' && connected) {
        try {
          const project = await db.getProject(projectId);
          setCurrentProject(project);
        } catch (error) {
          console.error('Error fetching project:', error);
          setCurrentProject(null);
        }
      } else {
        setCurrentProject(null);
      }
    };

    fetchProject();
  }, [projectId, connected]);
  
  // Handle mode toggle
  const handleModeChange = async (newMode) => {
    if (!newMode || !currentProject || !projectId) return;
    
    console.log('🔄 Switching mode to:', newMode);
    
    // Update local state
    setCurrentProject({ ...currentProject, mode: newMode });
    
    // Save to database
    try {
      await apiProjects.update(projectId, { mode: newMode, updated_at: new Date().toISOString() }, walletAddress);
      console.log('✅ Mode saved successfully');
    } catch (err) {
      console.error('❌ Error updating mode:', err);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBarStyled position="fixed" open={open}>
        <Toolbar sx={{ px: { xs: 1, sm: 2 }, minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              background: theme.colors.primaryAlpha10,
              borderRadius: '12px',
              padding: { xs: '6px 8px', sm: '8px 16px' },
              marginRight: { xs: 1, sm: 3 },
              border: `1px solid ${theme.colors.borderPrimary}`,
              backdropFilter: 'blur(10px)',
              boxShadow: `0 0 20px ${theme.colors.primaryAlpha20}`
            }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 2,
                boxShadow: `0 2px 8px ${theme.colors.primaryAlpha30}`
              }}>
                <Typography sx={{ 
                  color: '#fff', 
                  fontWeight: 'bold', 
                  fontSize: '1.2rem',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}>
                  💀
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                noWrap 
                component="div" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', sm: '1.2rem' },
                  color: theme.colors.primary,
                  letterSpacing: '1px',
                  textShadow: `0 0 10px ${theme.colors.primaryAlpha50}`,
                  fontFamily: '"Orbitron", "Inter", sans-serif',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                SOLANA DEADS
              </Typography>
            </Box>
            
            {/* Project Breadcrumb */}
            {currentProject && (
              <Box sx={{
                background: theme.colors.primaryAlpha10,
                borderRadius: '16px',
                padding: { xs: '4px 8px', sm: '6px 16px' },
                border: `1px solid ${theme.colors.borderSubtle}`,
                backdropFilter: 'blur(10px)',
                maxWidth: { xs: '200px', sm: 'none' },
                overflow: 'hidden'
              }}>
                <Breadcrumbs 
                  separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.4)' }} />}
                  sx={{ 
                    '& .MuiBreadcrumbs-separator': {
                      margin: '0 8px'
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="rgba(255,255,255,0.7)"
                    component={Link}
                    to="/projects"
                    sx={{ 
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        color: theme.colors.primary,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Projects
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon 
                      fontSize="small" 
                      sx={{ 
                        color: theme.colors.primary,
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                      }} 
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        color: theme.colors.primary,
                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      {currentProject.name}
                    </Typography>
                    {/* Animated/Mixed Project Indicator */}
                    {currentProject.allow_mixed_output ? (
                      <Tooltip title="Mixed Static/Animated Collection" arrow>
                        <Chip
                          icon={<MovieIcon sx={{ fontSize: '0.875rem' }} />}
                          label="Mixed"
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.8rem',
                            background: 'linear-gradient(135deg, #dc2626 0%, #673ab7 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            '& .MuiChip-icon': {
                              color: '#fff'
                            }
                          }}
                        />
                      </Tooltip>
                    ) : isAnimatedProject(currentProject.output_format) ? (
                      <Tooltip title="Animated NFT Project (MP4/WebM/GIF)" arrow>
                        <Chip
                          icon={<MovieIcon sx={{ fontSize: '0.875rem' }} />}
                          label="Animated"
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.8rem',
                            background: 'linear-gradient(135deg, #dc2626 0%, #f57c00 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            '& .MuiChip-icon': {
                              color: '#fff'
                            }
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Static Image Project (PNG/JPG/WebP)" arrow>
                        <Chip
                          icon={<ImageIcon sx={{ fontSize: '0.875rem' }} />}
                          label="Static"
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.8rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.7)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            fontWeight: 600,
                            '& .MuiChip-icon': {
                              color: 'rgba(255, 255, 255, 0.7)'
                            }
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Breadcrumbs>
              </Box>
            )}
          </Box>
          
          {/* Profile Settings Button */}
          {connected && (
            <Tooltip title="Profile Settings">
              <IconButton
                onClick={() => setProfileSettingsOpen(true)}
                sx={{
                  mr: 2,
                  color: theme.colors.textSecondary,
                  '&:hover': {
                    color: theme.colors.primary,
                    bgcolor: theme.colors.primaryAlpha10,
                  }
                }}
              >
                <PersonIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Wallet Connection Button */}
          {connected ? (
            <Box>
              <Button
                variant="contained"
                startIcon={<WalletIcon />}
                endIcon={<ExpandMoreIcon />}
                onClick={handleWalletMenu}
                sx={{
                  borderRadius: '16px',
                  textTransform: 'none',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`,
                  border: `1px solid ${theme.colors.borderPrimary}`,
                  backdropFilter: 'blur(10px)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: `0 4px 20px ${theme.colors.primaryAlpha30}`,
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: `0 8px 32px ${theme.colors.primaryAlpha50}`,
                    background: `linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.primary} 100%)`,
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* Blockchain Logo */}
                  <Box
                    component="img"
                    src={blockchainInfo.logo}
                    alt={blockchainInfo.name}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography sx={{ fontSize: '0.75rem', opacity: 0.8, lineHeight: 1 }}>
                      {blockchainInfo.name} • {walletName || 'Wallet'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1 }}>
                      {formatAddress(walletAddress)}
                    </Typography>
                  </Box>
                </Box>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: '12px',
                    background: 'rgba(30, 30, 30, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(39, 48, 63, 0.5)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  }
                }}
              >
                <MenuItem 
                  onClick={handleCopyAddress}
                  sx={{
                    borderRadius: '8px',
                    margin: '4px',
                    '&:hover': {
                      background: 'rgba(39, 48, 63, 0.4)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <CopyIcon fontSize="small" sx={{ color: '#dc2626' }} />
                  </ListItemIcon>
                  <ListItemText>{copied ? 'Copied!' : 'Copy Address'}</ListItemText>
                </MenuItem>
                <MenuItem 
                  onClick={handleDisconnect}
                  sx={{
                    borderRadius: '8px',
                    margin: '4px',
                    '&:hover': {
                      background: 'rgba(244, 67, 54, 0.1)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <DisconnectIcon fontSize="small" sx={{ color: '#f44336' }} />
                  </ListItemIcon>
                  <ListItemText>Disconnect</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={<WalletIcon />}
              onClick={() => setWalletModalOpen(true)}
              sx={{
                borderRadius: '16px',
                textTransform: 'none',
                px: { xs: 2, sm: 4 },
                py: { xs: 1, sm: 1.5 },
                minWidth: { xs: 'auto', sm: 'auto' },
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px) scale(1.02)',
                  boxShadow: '0 8px 32px rgba(220, 38, 38, 0.4)',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Connect Wallet
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Connect
              </Box>
            </Button>
          )}
        </Toolbar>
      </AppBarStyled>
      
      <StyledDrawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
      >
        <DrawerHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #dc2626, #991b1b)',
              boxShadow: '0 0 10px rgba(220, 38, 38, 0.5)'
            }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
              Navigation
            </Typography>
          </Box>
          <IconButton 
            onClick={handleDrawerClose}
            sx={{
              color: theme.colors.textSecondary,
              '&:hover': {
                background: 'rgba(39, 48, 63, 0.4)',
                color: '#dc2626'
              }
            }}
          >
            {muiTheme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider sx={{ borderColor: 'rgba(39, 48, 63, 0.5)' }} />
        
        {/* Back to Projects button when viewing a project */}
        {currentProject && projectId && projectId !== 'new' && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              onClick={() => navigate('/app/projects')}
              sx={{
                borderColor: theme.colors.borderPrimary,
                color: theme.colors.primary,
                '&:hover': {
                  borderColor: theme.colors.primary,
                  backgroundColor: theme.colors.primaryAlpha10,
                },
              }}
            >
              Back to Projects
            </Button>
            <Divider sx={{ borderColor: 'rgba(39, 48, 63, 0.5)', mt: 2 }} />
          </Box>
        )}
        
        <List sx={{ px: 1.5, py: 1 }}>
          {/* Show project menu items if viewing a project, otherwise show default menu */}
          {currentProject && projectId && projectId !== 'new' ? (
            <>
              {/* Render each section */}
              {['build', 'generate', 'analyze', 'project'].map((sectionKey) => {
                const section = getProjectMenuItems(projectId)[sectionKey];
                // In simple mode, hide Paths from build section items
                const items = sectionKey === 'build' && currentProject?.mode === 'simple'
                  ? section.items.filter(item => item.text !== 'Paths')
                  : section.items;

                return (
                  <React.Fragment key={sectionKey}>
                    {/* Section header */}
                    <ListItemButton
                      onClick={() => setSectionsOpen(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
                      sx={{
                        borderRadius: '10px',
                        mb: 0.5,
                        py: 1,
                        px: 1.5,
                        minHeight: 40,
                      }}
                    >
                      <ListItemText
                        primary={section.title}
                        sx={{
                          '& .MuiTypography-root': {
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: theme.colors.textSecondary,
                          }
                        }}
                      />
                      {sectionsOpen[sectionKey] ? <ExpandLessIcon sx={{ fontSize: 20, color: theme.colors.textMuted }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: theme.colors.textMuted }} />}
                    </ListItemButton>
                    <Collapse in={sectionsOpen[sectionKey]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ mb: 1 }}>
                        {items.map((item) => {
                          const isActive = location.pathname === item.path;
                          return (
                            <ListItem
                              key={item.text}
                              disablePadding
                              component={Link}
                              to={item.path}
                              sx={{
                                textDecoration: 'none',
                                color: 'inherit',
                                mb: 0.25,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                position: 'relative',
                                '&.active': {
                                  background: theme.colors.primaryAlpha10,
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: '20%',
                                    bottom: '20%',
                                    width: '3px',
                                    background: theme.colors.primary,
                                    borderRadius: '0 3px 3px 0'
                                  }
                                },
                                '&:hover': {
                                  background: 'rgba(255, 255, 255, 0.04)',
                                },
                                transition: 'all 0.15s ease',
                              }}
                              className={isActive ? 'active' : ''}
                            >
                              <ListItemButton sx={{ borderRadius: '8px', py: 1, px: 1.5 }}>
                                <ListItemIcon sx={{
                                  color: isActive ? theme.colors.primary : theme.colors.textMuted,
                                  minWidth: 36,
                                  '& .MuiSvgIcon-root': { fontSize: '1.25rem' }
                                }}>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.text}
                                  sx={{
                                    '& .MuiTypography-root': {
                                      fontWeight: isActive ? 600 : 500,
                                      fontSize: '0.9rem',
                                      color: isActive ? theme.colors.textPrimary : theme.colors.textSecondary,
                                    }
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            /* Default menu items when not in a project */
            defaultMenuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItem
                  key={item.text}
                  disablePadding
                  component={item.action ? 'div' : Link}
                  to={item.path}
                  onClick={item.action === 'profile' ? () => setProfileSettingsOpen(true) : undefined}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    mb: 0.5,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: item.action ? 'pointer' : 'default',
                    '&.active': {
                      background: theme.colors.primaryAlpha10,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: '3px',
                        background: theme.colors.primary,
                        borderRadius: '0 3px 3px 0'
                      }
                    },
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.04)',
                    },
                    transition: 'all 0.15s ease',
                  }}
                  className={isActive ? 'active' : ''}
                >
                  <ListItemButton sx={{ borderRadius: '10px', py: 1.25, px: 2 }}>
                    <ListItemIcon sx={{
                      color: isActive ? theme.colors.primary : theme.colors.textMuted,
                      minWidth: 40,
                      '& .MuiSvgIcon-root': { fontSize: '1.3rem' }
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: isActive ? 600 : 500,
                          fontSize: '0.95rem',
                          color: isActive ? theme.colors.textPrimary : theme.colors.textSecondary,
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </List>
        
        {/* Bottom section with user info or additional actions */}
        <Box sx={{ 
          mt: 'auto', 
          p: 2, 
          borderTop: '1px solid rgba(39, 48, 63, 0.5)',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          {/* Mode Toggle and Help */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <HelpButton userMode={currentProject?.mode || userMode} variant="icon" />
            <ToggleButtonGroup
              value={currentProject?.mode || userMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode) {
                  // Update both user preference and current project
                  setUserMode(newMode);
                  if (currentProject) {
                    handleModeChange(newMode);
                  }
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: theme.colors.textSecondary,
                  borderColor: 'rgba(255,255,255,0.2)',
                  '&.Mui-selected': {
                    bgcolor: theme.colors.primaryAlpha20,
                    color: theme.colors.primary,
                  }
                }
              }}
            >
              <ToggleButton value="simple" sx={{ px: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                Simple
              </ToggleButton>
              <ToggleButton value="advanced" sx={{ px: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                Advanced
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          <Typography variant="caption" sx={{ color: theme.colors.textMuted, display: 'block', textAlign: 'center' }}>
            GraveForge v2.0
          </Typography>
        </Box>
      </StyledDrawer>
      
      <Main open={open} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', pb: { xs: '80px', md: 0 } }}>
        <DrawerHeader />
        <Box sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          width: '100%',
          flex: 1,
          maxWidth: '1600px',
          mx: 'auto',
        }}>
          <Outlet />
        </Box>

        {/* Dashboard Footer */}
        <DashboardFooter />
      </Main>
      {/* Floating Help Button */}
      <HelpButton userMode={currentProject?.mode || userMode} variant="fab" />
      
      {/* Wallet Connection Modal */}
      <WalletModal 
        open={walletModalOpen} 
        onClose={() => setWalletModalOpen(false)} 
      />
      
      {/* Profile Settings Dialog */}
      <ProfileSettings
        open={profileSettingsOpen}
        onClose={() => setProfileSettingsOpen(false)}
      />
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={handleMobileNav} />
    </Box>
  );
}
